import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { StageProgress, StepCard, SelectField } from '../components/form/FormStepUI';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { readMatchParams } from '../utils/matchParams';

/**
 * Entry point for the match funnel. Collects the three things that determine
 * which programs we can match against, then hands off to the questionnaire.
 */
export function MatchStartPage() {
  usePageMeta('Find My Match | Online College Guide', 'Compare accredited online degree programs. Get matched by career goals, schedule, and budget.');
  const navigate = useNavigate();
  // Start certification here, at the top of the funnel, so the certificate
  // covers the whole interaction rather than only the final screen.
  useLeadTracking();
  // Honor pre-populated links so campaign traffic skips choices it already made.
  const prefill = readMatchParams();
  const [degreeInterest, setDegreeInterest] = useState(prefill.degreeInterest || '');
  const [areaOfStudy, setAreaOfStudy] = useState(prefill.areaOfStudy || '');
  const [specialization, setSpecialization] = useState(prefill.specialization || '');
  const [error, setError] = useState('');
  const programOptions = specializationOptions.filter(spec => spec.focusValue === areaOfStudy);
  const handleAreaChange = (value: string) => {
    setAreaOfStudy(value);
    // A program from the previous area would no longer be valid.
    setSpecialization('');
    setError('');
  };
  const handleContinue = () => {
    if (!degreeInterest) {
      setError('Please select the degree level you are interested in.');
      return;
    }
    if (!areaOfStudy) {
      setError('Please select an area of study.');
      return;
    }
    if (!specialization) {
      setError('Please select a program.');
      return;
    }
    setError('');
    navigate('/request-information/form', {
      state: {
        degreeInterest,
        areaOfStudy,
        specialization
      }
    });
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2226" data-mp="2hmdmzvnmjh9e">
      <Header data-id="element-2227" data-mp="2hkssu75820c2" />
      <main className="flex-1 py-6 sm:py-10 px-4" data-id="element-2228" data-mp="2hogl72yxkgp7">
        <div className="max-w-3xl mx-auto" data-id="element-2229" data-mp="2cmiwvuboavpe">
          <StageProgress currentStep={1} data-id="element-2230" data-mp="2cpwxax0s0uu7" />

          <div className="mt-2 mb-4" data-id="element-2231" data-mp="2rl36fsv659rr">
            <button type="button" onClick={() => navigate('/')} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2232" data-mp="2rk1rhdb1834r">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2233" data-mp="s7pcjzylg9e0" />
              Back
            </button>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-2234" data-mp="33uj0jav2g8ts">
            <StepCard title="What would you like to study?" subtitle="Tell us the program you have in mind and we'll match you with accredited online colleges that offer it." onContinue={handleContinue} ctaLabel="Continue" info="Matching costs you nothing, and we only work with accredited partner schools. You choose whether to talk with any of them." error={error} data-id="element-2235" data-mp="2z3khi3ip0wmu">
              <div className="space-y-5" data-id="element-2236" data-mp="9f0nqzn586fb">
                <SelectField id="degree-level" label="Degree Level" value={degreeInterest} onChange={v => {
                setDegreeInterest(v);
                setError('');
              }} options={degreeInterestOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2237" data-mp="9g215armt507" />

                <SelectField id="area-of-study" label="Area of Study" value={areaOfStudy} onChange={handleAreaChange} options={focusOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2238" data-mp="1whgz7f1auc9t" />

                <SelectField id="program" label="Program" value={specialization} onChange={v => {
                setSpecialization(v);
                setError('');
              }} disabled={!areaOfStudy} placeholder={areaOfStudy ? '-- Please Select --' : 'Select an area of study first'} options={programOptions.map(opt => ({
                label: opt.type,
                value: opt.value
              }))} data-id="element-2239" data-mp="1hsu9qkhyk44v" />
              </div>
            </StepCard>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-2240" data-mp="2wq6vjqenoznp" />
    </div>;
}